import { useState, useMemo, useEffect, useRef } from 'react'
import {
  Dialog,
  DialogContent,
  IconButton,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material'
import { Close } from '@mui/icons-material'
import { ChatProvider } from '../context/ChatProvider'
import { ChatWindow } from './ChatWindow'
import {
  requestTherapistToken,
  requestPatientToken,
  createConversation,
  extractTherapistId,
  extractPatientId,
  resolveTwilioConversationSid,
} from '../services/chatApi'
import { requestAgencyToken } from '../services/chatApi'
import type {
  ChatConversation,
  PatientInfo,
  TherapistInfo,
} from '@/chat/services/chatApi'
import { UserRole } from '@/types'
import { useAppSelector } from '@/store'
import { useTheme } from '@mui/material/styles'

interface ChatDialogProps {
  open: boolean
  onClose: () => void
  patientId: string | PatientInfo
  therapistId: string | TherapistInfo
  patientDisplayName?: string
  therapistDisplayName?: string
  patientAvatarUrl?: string
  therapistAvatarUrl?: string
  participantDisplayName?: string
  participantAvatarUrl?: string
  initialConversation?: ChatConversation | null
}

const getEntityDisplayName = (
  entity: string | { firstName?: string; lastName?: string },
  fallback: string
): string => {
  if (typeof entity === 'string') return fallback
  const first = entity.firstName?.trim() ?? ''
  const last = entity.lastName?.trim() ?? ''
  const combined = `${first} ${last}`.trim()
  return combined || fallback
}

const getEntityAvatarUrl = (
  entity: string | { profilePhotoUrl?: string }
): string | undefined => {
  if (typeof entity === 'string') return undefined
  return entity.profilePhotoUrl
}

const getCurrentUserDisplayName = (user?: {
  firstName?: string | null
  lastName?: string | null
  email?: string | null
  id?: string | null
}): string => {
  if (!user) return 'You'
  const first = user.firstName?.trim() ?? ''
  const last = user.lastName?.trim() ?? ''
  const combined = `${first} ${last}`.trim()
  if (combined) return combined
  if (user.email) return user.email
  if (user.id) return user.id
  return 'You'
}

export const ChatDialog = ({
  open,
  onClose,
  patientId,
  therapistId,
  patientDisplayName,
  therapistDisplayName,
  patientAvatarUrl,
  therapistAvatarUrl,
  participantDisplayName: overrideParticipantDisplayName,
  participantAvatarUrl: overrideParticipantAvatarUrl,
  initialConversation,
}: ChatDialogProps) => {
  const { user } = useAppSelector(state => state.auth)
  const [conversation, setConversation] = useState<ChatConversation | null>(
    null
  )
  const [conversationLoading, setConversationLoading] = useState(false)
  const [conversationError, setConversationError] = useState<string | null>(
    null
  )

  const currentUserId = user?.id ?? ''
  const role = user?.role ?? null
  const isTherapist = role === UserRole.THERAPIST
  // Agencies chat with patients, so treat them the same as therapists
  // for the purpose of determining who the "other" participant is
  const isAgency = role === UserRole.AGENCY
  const showPatientAsParticipant = isTherapist || isAgency

  // Extract IDs
  const therapistIdString = extractTherapistId(therapistId)
  const patientIdString = extractPatientId(patientId)

  // Determine participant info based on current user role
  const participantEntity = showPatientAsParticipant ? patientId : therapistId

  // Extract display name - prefer explicit override prop, then role-specific prop, then entity, then fallback
  const participantDisplayName =
    overrideParticipantDisplayName ??
    ((showPatientAsParticipant ? patientDisplayName : therapistDisplayName) ??
      getEntityDisplayName(participantEntity, showPatientAsParticipant ? 'Patient' : 'Provider'))

  // Extract avatar URL - prioritize prop, then entity
  const entityAvatarUrl = getEntityAvatarUrl(participantEntity)
  const participantAvatarUrl =
    overrideParticipantAvatarUrl ??
    ((showPatientAsParticipant ? patientAvatarUrl : therapistAvatarUrl) ?? entityAvatarUrl)

  const currentUserDisplayName = getCurrentUserDisplayName(user ?? undefined)

  // Refs to prevent duplicate API calls in Strict Mode
  const isInitializingRef = useRef(false)
  const abortControllerRef = useRef<AbortController | null>(null)

  // Create conversation when dialog opens
  useEffect(() => {
    if (!open || !currentUserId) {
      setConversation(null)
      setConversationError(null)
      isInitializingRef.current = false
      // Cancel any pending request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
        abortControllerRef.current = null
      }
      return
    }

    // If an initial conversation is provided, use it and skip creating one
    if (initialConversation) {
      setConversation(initialConversation)
      return
    }

    // Prevent duplicate calls
    if (isInitializingRef.current) {
      return
    }

    // Cancel any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    // Create new abort controller
    const abortController = new AbortController()
    abortControllerRef.current = abortController

    const initializeConversation = async () => {
      isInitializingRef.current = true
      setConversationLoading(true)
      setConversationError(null)

      try {
        const createdConversation = await createConversation(
          {
            therapistId: therapistIdString,
            patientId: patientIdString,
          },
          abortController.signal
        )

        // Check if request was aborted
        if (abortController.signal.aborted) {
          return
        }

        // Create a ChatConversation object compatible with our system
        const chatConversation: ChatConversation = {
          conversationSid: resolveTwilioConversationSid(
            createdConversation.conversationSid,
            createdConversation.conversationId
          ),
          conversationId: createdConversation.conversationId,
          therapistId: therapistId as string | TherapistInfo,
          patientId: patientId as string | PatientInfo,
          reused: createdConversation.reused,
          agencyId: createdConversation.agencyId,
        }

        setConversation(chatConversation)
      } catch (error) {
        // Don't set error if request was aborted
        if (abortController.signal.aborted) {
          return
        }
        console.error('Failed to create conversation:', error)
        setConversationError(
          error instanceof Error
            ? error.message
            : 'Failed to initialize chat. Please try again.'
        )
      } finally {
        if (!abortController.signal.aborted) {
          setConversationLoading(false)
          isInitializingRef.current = false
        }
        abortControllerRef.current = null
      }
    }

    initializeConversation()

    // Cleanup function
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
        abortControllerRef.current = null
      }
      isInitializingRef.current = false
    }
  }, [
    open,
    currentUserId,
    therapistIdString,
    patientIdString,
    therapistId,
    patientId,
    initialConversation,
  ])

  const tokenFetcher = useMemo(() => {
    if (!currentUserId) return undefined

    // If current user is an agency, try to resolve agency id from user object
    if (role === UserRole.AGENCY) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const u = user as any
      const agencyId =
        u?.agencyId || (typeof u?.agency === 'string' ? u?.agency : u?.agency?._id) || currentUserId
      return (signal?: AbortSignal) => requestAgencyToken(agencyId, signal)
    }

    return isTherapist
      ? (signal?: AbortSignal) => requestTherapistToken(currentUserId, signal)
      : (signal?: AbortSignal) => requestPatientToken(currentUserId, signal)
  }, [currentUserId, isTherapist, role, user])

  const conversationResolver = useMemo(() => {
    if (!conversation || !currentUserId) return undefined

    return async (signal?: AbortSignal): Promise<string> => {
      const sid = resolveTwilioConversationSid(
        conversation.conversationSid,
        conversation.conversationId
      )
      if (!sid) {
        throw new Error('Conversation SID is missing')
      }
      if (!sid.startsWith('CH')) {
        throw new Error(
          `Server returned invalid Twilio conversation SID "${sid}".`
        )
      }
      return sid
    }
  }, [conversation, currentUserId])

  const conversationId = resolveTwilioConversationSid(
    conversation?.conversationSid,
    conversation?.conversationId
  )

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          maxHeight: '90vh',
          borderRadius: 3,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          position: 'relative',
          alignItems: 'center',
          justifyContent: 'center',
          px: 3,
          py: 2,
        }}
      >
        <Typography variant="h6">Send Message</Typography>
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', top: 8, right: 8 }}
        >
          <Close />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          flex: 1,
          p: '0 !important',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {conversationLoading ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              gap: 2,
            }}
          >
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              Connecting to chat...
            </Typography>
          </Box>
        ) : conversationError ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              gap: 2,
              p: 3,
            }}
          >
            <Typography variant="h6" color="error">
              Unable to start chat
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {conversationError}
            </Typography>
          </Box>
        ) : conversation &&
          tokenFetcher &&
          conversationResolver &&
          conversationId ? (
          <ChatProvider
            key={conversationId}
            config={{
              tokenFetcher,
              conversationResolver,
              enableDebugLogging: import.meta.env.DEV,
              participantDisplayName,
              participantAvatarUrl,
              currentUserDisplayName,
            }}
          >
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                bgcolor: '#F6F7F9',
              }}
            >
              <ChatWindow />
            </Box>
          </ChatProvider>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              gap: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Please log in to start messaging
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}
